import { renderQuestionnaireAnswers } from "./questionnaire";
/**
 * The civic record as a timeline (prototype `person-page`, direction B).
 * A run is one campaign event titled by its office; its ballots are rows
 * beneath it, latest Election date first. How the run ended, incumbency and
 * the endorsement sit once at the card's top right; an endorsement moves onto
 * the dated rows only when its standing changed between them. The ballot that
 * ended a lost run carries the lost treatment.
 */
import type { PublicCandidacySection, PublicSeatAssignmentSection } from "../api";
import { h } from "../dom";
import { candidacyStatusLabel, endorsedBadge, incumbentBadge, partyBadge, partyName, responseBadge, revokedBadge } from "../labels";
import { electionHref } from "../links";
import { formatDate } from "../model";
import { ballotLabel, campaignStatusPills, endorsementSummary, newestBallotsFirst, officeHeading, runOutcome, type TimelineEntry } from "../person-model";
import { contactLinks } from "./contacts";

export function renderCampaignQuestionnaire(c: Pick<PublicCandidacySection, "questionnaire">, key: string): HTMLElement {
  const q = c.questionnaire;
  // Before the first publish date: the questionnaire's message, nothing else.
  if (q?.notice) {
    return h(
      "div",
      { class: "bsc-questionnaire bsc-questionnaire--unavailable" },
      h("div", { class: "bsc-questionnaire__header" }, h("span", { class: "bsc-questionnaire__title" }, "Questionnaire")),
      h("p", { class: "bsc-questionnaire__notice" }, q.notice.message)
    );
  }
  if (!q || q.status !== "completed" || !q.answers) {
    return h(
      "div",
      { class: "bsc-questionnaire bsc-questionnaire--unavailable" },
      h(
        "div",
        { class: "bsc-questionnaire__header" },
        // The status pill says it all: "No published answers" only repeated "No response".
        h("span", { class: "bsc-questionnaire__title" }, "Questionnaire"),
        responseBadge(q?.status ?? "no_response")
      )
    );
  }
  const disclosure = h("span", { class: "bsc-questionnaire__disclosure" }, "View answers");
  const details = h(
    "details",
    { class: "bsc-questionnaire__details" },
    h("summary", { class: "bsc-questionnaire__summary" }, disclosure, h("span", { class: "bsc-sr-only" }, ` from ${q.title}`)),
    renderQuestionnaireAnswers(q, key)
  );
  details.addEventListener("toggle", () => {
    disclosure.textContent = details.open ? "Hide answers" : "View answers";
  });
  return h(
    "div",
    { class: "bsc-questionnaire" },
    h(
      "div",
      { class: "bsc-questionnaire__header" },
      h("span", { class: "bsc-questionnaire__title" }, "Questionnaire"),
      responseBadge(q.status),
      // The Person view adds "Compare candidates" here once it knows the Race
      // has another completed Response.
      h("span", { class: "bsc-questionnaire__actions" })
    ),
    details
  );
}

/**
 * The run at a glance, top right: how it ended, incumbency, a current run's
 * standing (the Election page's pills: "Won primary", "Announced"), and BSC's.
 */
function campaignStanding(entry: Extract<TimelineEntry, { kind: "campaign" }>, card: "endorsed" | "revoked" | null): HTMLElement | null {
  const status = entry.current
    ? campaignStatusPills(entry.candidacy.ballots, candidacyStatusLabel).map((pill) =>
        pill.kind === "result"
          ? h("span", { class: "bsc-stage-tag", "data-stage-status": pill.status }, pill.text)
          : h("span", { class: "bsc-badge bsc-badge--candidacy" }, pill.text))
    : [];
  const pills = [
    entry.outcome === "won" ? h("span", { class: "bsc-badge bsc-badge--won" }, "Elected") : null,
    entry.candidacy.isIncumbent ? incumbentBadge() : null,
    ...status,
    card === "endorsed" ? endorsedBadge() : card === "revoked" ? revokedBadge() : null,
  ].filter((p): p is HTMLElement => p !== null);
  return pills.length ? h("p", { class: "bsc-campaign__standing" }, pills) : null;
}

function campaignCard(entry: Extract<TimelineEntry, { kind: "campaign" }>, page: URL, owner: string): HTMLElement {
  const c = entry.candidacy;
  const { ballot: endedOn } = runOutcome(c.ballots);
  const endorsement = endorsementSummary(c.ballots);
  const cycleLink = c.cycle?.publicUrl ? h("a", { class: "bsc-campaign__cycle-link", href: electionHref(page, { slug: c.cycle.slug, publicUrl: c.cycle.publicUrl }) }, c.cycle.name ?? "View election") : c.cycle?.name ?? null;
  const context = entry.current ? "Current campaign" : "Campaign";
  const heading = officeHeading({ role: c.officeTitle, place: c.race?.name ?? null, body: c.body?.name ?? null });
  // The Election page's Party pill, held open: a card has the room, and a phone no hover.
  const party = partyName(c.party);
  const partyPill = party ? partyBadge(party) : null;
  partyPill?.classList.add("bsc-party--open");
  return h(
    "article",
    {
      class: `bsc-campaign bsc-campaign--${entry.current ? "current" : "past"}`,
      "aria-labelledby": `${entry.key}-title`,
      "data-race-slug": c.race?.slug ?? null,
      "data-cycle-slug": c.cycle?.slug ?? null,
    },
    h(
      "header",
      { class: "bsc-campaign__head" },
      h("p", { class: "bsc-campaign__context" }, context, cycleLink ? [" · ", cycleLink] : null),
      h("h2", { class: "bsc-campaign__race", id: `${entry.key}-title` }, heading.title),
      heading.subtitle ? h("p", { class: "bsc-campaign__office" }, heading.subtitle) : null,
      party ? h("p", { class: "bsc-campaign__party" }, partyPill, h("span", { class: "bsc-sr-only" }, `${party} Party`)) : null,
      campaignStanding(entry, endorsement.card)
    ),
    c.ballots.length
      ? h(
          "ol",
          { class: "bsc-campaign__ballots", "aria-label": "Election dates, latest first" },
          newestBallotsFirst(c.ballots).map((b) =>
            h(
              "li",
              {
                class: "bsc-campaign__ballot",
                "data-ballot-stage": b.stage,
                "data-status": b.status,
                "data-outcome": b.slug === endedOn ? entry.outcome : null,
              },
              h("span", { class: "bsc-campaign__ballot-label" }, ballotLabel(b)),
              " ",
              h("time", { class: "bsc-campaign__ballot-date", datetime: b.date }, `${b.label}, ${formatDate(b.date)}`),
              // Per-date pills only when the standing changed between dates.
              endorsement.perBallot ? (b.endorsed ? [" ", endorsedBadge()] : b.endorsementRevoked ? [" ", revokedBadge()] : null) : null
            )
          )
        )
      : null,
    // Campaign contacts only while the candidacy is current.
    entry.current ? contactLinks(c.contact, owner, "Campaign contacts") : null,
    renderCampaignQuestionnaire(c, entry.key)
  );
}

function officeCard(office: PublicSeatAssignmentSection, key: string, owner: string): HTMLElement {
  const { title, subtitle } = officeHeading({ role: office.roleTitle, place: office.seat.name, body: office.body.name });
  return h(
    "article",
    { class: `bsc-office${subtitle ? "" : " bsc-office--titled"}`, "aria-labelledby": `${key}-title` },
    h("p", { class: "bsc-office__context" }, "Office currently held"),
    h("h2", { class: "bsc-office__title", id: `${key}-title` }, title),
    subtitle ? h("p", { class: "bsc-office__body" }, subtitle) : null,
    contactLinks(office.contact, owner, "Office contacts")
  );
}

export function renderTimeline(entries: TimelineEntry[], page: URL, owner: string): HTMLElement {
  return h(
    "ol",
    { class: "bsc-timeline", "aria-label": "Civic record" },
    entries.map((entry) =>
      h(
        "li",
        {
          class: "bsc-timeline__event",
          id: `bsc-${entry.key}`,
          "data-key": entry.key,
          "data-kind": entry.kind,
          "data-outcome": entry.kind === "campaign" ? entry.outcome : null,
        },
        h("span", { class: "bsc-timeline__date", "aria-hidden": "true" }, entry.dateParts.map((p) => h("span", null, p))),
        h(
          "div",
          { class: "bsc-timeline__card" },
          entry.kind === "campaign" ? campaignCard(entry, page, owner) : officeCard(entry.office, entry.key, owner)
        )
      )
    )
  );
}
